import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { api } from '@/App';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Switch } from '../components/ui/switch';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { toast } from 'sonner';
import { 
  Building, 
  ArrowLeft, 
  Save, 
  Loader2, 
  Settings, 
  MapPin, 
  CheckCircle, 
  Image, 
  Bed, 
  IndianRupee, 
  Tag,
  Star,
  Globe,
  Plus,
  Trash2,
  Map,
  Navigation,
  Plane,
  Train,
  Bus,
  Hospital,
  ShoppingBag,
  Utensils,
  Landmark,
  Upload,
  X,
  Search,
  Sparkles
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';

export default function PropertyConfigurePage({ user, isTab, tabId, tabData }) {
  // Support both route-based (useParams) and tab-based (tabData) usage
  const routeParams = useParams();
  const navigate = useNavigate();
  
  // Get propertyId from tabData (in-app tab) or route params (direct URL)
  const propertyId = tabData?.propertyId || routeParams.propertyId;
  
  const [property, setProperty] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [availableAmenities, setAvailableAmenities] = useState([]);
  const [uploadingHotelPhoto, setUploadingHotelPhoto] = useState(false);
  const hotelPhotoInputRef = useRef(null);
  
  const [listing, setListing] = useState({
    listing_enabled: true,
    booking_mode: 'use_default',
    payment_mode: 'use_default',
    advance_percentage: 20,
    featured: false,
    listing_title: '',
    listing_description: '',
    listing_photos: [],
    amenities: [],
    check_in_time: '14:00',
    check_out_time: '11:00',
    subdomain: '',
    custom_domain: '',
    room_allocations: {},
    discount_offers: [],
    room_types: [],
    location: {
      latitude: null,
      longitude: null,
      address: '',
      city: '',
      state: '',
      country: '',
      postal_code: '',
      google_maps_url: ''
    },
    nearby_places: []
  });

  // AI Place Search State
  const [placeSearchQuery, setPlaceSearchQuery] = useState('');
  const [placeSearchType, setPlaceSearchType] = useState('');
  const [placeSearchResults, setPlaceSearchResults] = useState([]);
  const [searchingPlaces, setSearchingPlaces] = useState(false);
  const [showSearchResults, setShowSearchResults] = useState(false);

  // AI-powered place search function
  const searchNearbyPlaces = async () => {
    if (!placeSearchQuery.trim()) {
      toast.error('Please enter a search query');
      return;
    }
    
    if (!listing.location?.latitude || !listing.location?.longitude) {
      toast.error('Please set property location first (latitude and longitude)');
      return;
    }
    
    setSearchingPlaces(true);
    setShowSearchResults(true);
    
    try {
      const response = await api.post('/places/search', {
        query: placeSearchQuery,
        latitude: listing.location.latitude,
        longitude: listing.location.longitude,
        radius: 20000,
        place_type: placeSearchType || null
      });
      
      setPlaceSearchResults(response.data.results || []);
      
      if (response.data.ai_enhanced) {
        toast.success(`Found ${response.data.total} places using AI-powered search`);
      } else {
        toast.success(`Found ${response.data.total} places`);
      }
    } catch (error) {
      console.error('Place search error:', error);
      toast.error('Failed to search places');
      setPlaceSearchResults([]);
    } finally {
      setSearchingPlaces(false);
    }
  };

  // Add place from search results
  const addPlaceFromSearch = (searchResult) => {
    const newPlace = {
      name: searchResult.name,
      type: searchResult.place_type || 'other',
      distance: searchResult.distance_km?.toString() || '',
      distance_unit: 'km',
      latitude: searchResult.latitude,
      longitude: searchResult.longitude,
      google_maps_url: searchResult.google_maps_url || ''
    };
    
    setListing({
      ...listing,
      nearby_places: [...(listing.nearby_places || []), newPlace]
    });
    
    toast.success(`Added "${searchResult.name}" to nearby places`);
  };

  useEffect(() => {
    if (propertyId) {
      loadPropertyData();
      loadAmenities();
    }
  }, [propertyId]);

  const loadPropertyData = async () => {
    try {
      setLoading(true);
      
      // Load listing data which includes property info
      const listingResponse = await api.get(`/portal/admin/listing/${propertyId}`);
      
      // Set property info from listing response
      setProperty({
        id: propertyId,
        name: listingResponse.data.hotel_name || listingResponse.data.listing_title || 'Property',
        city: listingResponse.data.hotel_city || listingResponse.data.location?.city || '',
        state: listingResponse.data.hotel_state || listingResponse.data.location?.state || ''
      });
      
      setListing({
        listing_enabled: listingResponse.data.listing_enabled ?? true,
        booking_mode: listingResponse.data.booking_mode || 'use_default',
        payment_mode: listingResponse.data.payment_mode || 'use_default',
        advance_percentage: listingResponse.data.advance_percentage || 20,
        featured: listingResponse.data.featured || false,
        listing_title: listingResponse.data.listing_title || '',
        listing_description: listingResponse.data.listing_description || '',
        listing_photos: listingResponse.data.listing_photos || [],
        amenities: listingResponse.data.amenities || [],
        check_in_time: listingResponse.data.check_in_time || '14:00',
        check_out_time: listingResponse.data.check_out_time || '11:00',
        subdomain: listingResponse.data.subdomain || '',
        custom_domain: listingResponse.data.custom_domain || '',
        room_allocations: listingResponse.data.room_allocations || {},
        discount_offers: listingResponse.data.discount_offers || [],
        room_types: listingResponse.data.room_types || [],
        location: listingResponse.data.location || {
          latitude: null,
          longitude: null,
          address: '',
          city: '',
          state: '',
          country: '',
          postal_code: '',
          google_maps_url: ''
        },
        nearby_places: listingResponse.data.nearby_places || []
      });
    } catch (error) {
      toast.error('Failed to load property data');
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const loadAmenities = async () => {
    try {
      const response = await api.get('/master/amenities');
      setAvailableAmenities(response.data || []);
    } catch (error) {
      console.error('Failed to load amenities:', error);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const { room_types, ...saveData } = listing;
      await api.put(`/portal/admin/listing/${propertyId}`, saveData);
      toast.success('Configuration saved successfully');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save configuration');
    } finally {
      setSaving(false);
    }
  };

  const uploadPhoto = async (file, photoType, entityId) => {
    const formData = new FormData();
    formData.append('file', file);
    const params = new URLSearchParams({ photo_type: photoType });
    if (entityId) params.append('entity_id', entityId);
    const response = await api.post(`/upload/portal-photo?${params.toString()}`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    });
    return response.data.photo_url;
  };

  const handleHotelPhotoUpload = async (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    setUploadingHotelPhoto(true);
    try {
      for (const file of files) {
        const url = await uploadPhoto(file, 'hotel', propertyId);
        setListing(prev => ({
          ...prev,
          listing_photos: [...(prev.listing_photos || []), url]
        }));
      }
      toast.success(`${files.length} photo(s) uploaded`);
    } catch (err) {
      toast.error('Failed to upload photo');
    } finally {
      setUploadingHotelPhoto(false);
      e.target.value = '';
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <Loader2 className="h-8 w-8 animate-spin text-orange-500" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header - Uses CSS theme variables */}
      <div 
        className="text-white sticky top-0 z-10"
        style={{ background: 'linear-gradient(to right, var(--theme-primary, #f97316), var(--theme-accent, #ea580c))' }}
      >
        <div className="max-w-6xl mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              {!isTab && (
                <Button
                  variant="ghost"
                  size="sm"
                  className="text-white hover:bg-white/20"
                  onClick={() => navigate('/admin/portal/listing')}
                >
                  <ArrowLeft className="h-4 w-4 mr-1" />
                  Back
                </Button>
              )}
              <div className="flex items-center gap-3">
                <div className="bg-white/20 p-2 rounded-lg">
                  <Building className="h-6 w-6" />
                </div>
                <div>
                  <h1 className="text-xl font-bold">{property?.name}</h1>
                  <p className="text-white/80 text-sm flex items-center gap-1">
                    <MapPin className="h-3 w-3" />
                    {property?.city}{property?.state ? `, ${property.state}` : ''}
                  </p>
                </div>
              </div>
            </div>
            <Button
              onClick={handleSave}
              disabled={saving}
              className="bg-white hover:bg-gray-100"
              style={{ color: 'var(--theme-primary, #f97316)' }}
            >
              {saving ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Save className="h-4 w-4 mr-2" />}
              Save Changes
            </Button>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-6xl mx-auto px-4 py-6">
        <Tabs defaultValue="basic" className="w-full">
          <TabsList className="grid w-full grid-cols-7 mb-6">
            <TabsTrigger value="basic"><Settings className="h-4 w-4 mr-1" /> Basic</TabsTrigger>
            <TabsTrigger value="location"><MapPin className="h-4 w-4 mr-1" /> Location</TabsTrigger>
            <TabsTrigger value="amenities"><CheckCircle className="h-4 w-4 mr-1" /> Amenities</TabsTrigger>
            <TabsTrigger value="photos"><Image className="h-4 w-4 mr-1" /> Photos</TabsTrigger>
            <TabsTrigger value="rooms"><Bed className="h-4 w-4 mr-1" /> Rooms</TabsTrigger>
            <TabsTrigger value="pricing"><IndianRupee className="h-4 w-4 mr-1" /> Pricing</TabsTrigger>
            <TabsTrigger value="discounts"><Tag className="h-4 w-4 mr-1" /> Discounts</TabsTrigger>
          </TabsList>

          {/* Basic Info Tab */}
          <TabsContent value="basic">
            <Card>
              <CardHeader>
                <CardTitle>Basic Information</CardTitle>
                <CardDescription>Configure listing details and booking settings</CardDescription>
              </CardHeader>
              <CardContent className="space-y-6">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label>Listing Title</Label>
                    <Input
                      value={listing.listing_title}
                      onChange={(e) => setListing({ ...listing, listing_title: e.target.value })}
                      placeholder={property?.name}
                    />
                  </div>
                  <div>
                    <Label>Subdomain</Label>
                    <Input
                      value={listing.subdomain}
                      onChange={(e) => setListing({ ...listing, subdomain: e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, '') })}
                      placeholder="my-hotel"
                    />
                  </div>
                </div>

                <div>
                  <Label>Description</Label>
                  <Textarea
                    value={listing.listing_description}
                    onChange={(e) => setListing({ ...listing, listing_description: e.target.value })}
                    placeholder="Describe your property..."
                    rows={4}
                  />
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label>Check-in Time</Label>
                    <Input
                      type="time"
                      value={listing.check_in_time}
                      onChange={(e) => setListing({ ...listing, check_in_time: e.target.value })}
                    />
                  </div>
                  <div>
                    <Label>Check-out Time</Label>
                    <Input
                      type="time"
                      value={listing.check_out_time}
                      onChange={(e) => setListing({ ...listing, check_out_time: e.target.value })}
                    />
                  </div>
                </div>

                <div className="flex items-center gap-4">
                  <div className="flex items-center gap-2">
                    <Switch
                      checked={listing.featured}
                      onCheckedChange={(checked) => setListing({ ...listing, featured: checked })}
                    />
                    <Label>Featured Property</Label>
                  </div>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Location Tab */}
          <TabsContent value="location">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Map className="h-5 w-5 text-orange-500" />
                  Property Location
                </CardTitle>
                <CardDescription>Set your property's exact location on the map</CardDescription>
              </CardHeader>
              <CardContent className="space-y-6">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label>Latitude</Label>
                    <Input
                      type="number"
                      step="any"
                      value={listing.location?.latitude || ''}
                      onChange={(e) => setListing({
                        ...listing,
                        location: { ...listing.location, latitude: parseFloat(e.target.value) || null }
                      })}
                      placeholder="e.g., 12.9716"
                    />
                  </div>
                  <div>
                    <Label>Longitude</Label>
                    <Input
                      type="number"
                      step="any"
                      value={listing.location?.longitude || ''}
                      onChange={(e) => setListing({
                        ...listing,
                        location: { ...listing.location, longitude: parseFloat(e.target.value) || null }
                      })}
                      placeholder="e.g., 77.5946"
                    />
                  </div>
                </div>

                <div>
                  <Label>Full Address</Label>
                  <Textarea
                    value={listing.location?.address || ''}
                    onChange={(e) => setListing({
                      ...listing,
                      location: { ...listing.location, address: e.target.value }
                    })}
                    placeholder="Enter complete property address"
                    rows={2}
                  />
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label>City</Label>
                    <Input
                      value={listing.location?.city || ''}
                      onChange={(e) => setListing({
                        ...listing,
                        location: { ...listing.location, city: e.target.value }
                      })}
                    />
                  </div>
                  <div>
                    <Label>State/Province</Label>
                    <Input
                      value={listing.location?.state || ''}
                      onChange={(e) => setListing({
                        ...listing,
                        location: { ...listing.location, state: e.target.value }
                      })}
                    />
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label>Country</Label>
                    <Input
                      value={listing.location?.country || ''}
                      onChange={(e) => setListing({
                        ...listing,
                        location: { ...listing.location, country: e.target.value }
                      })}
                    />
                  </div>
                  <div>
                    <Label>Postal Code</Label>
                    <Input
                      value={listing.location?.postal_code || ''}
                      onChange={(e) => setListing({
                        ...listing,
                        location: { ...listing.location, postal_code: e.target.value }
                      })}
                    />
                  </div>
                </div>

                <div>
                  <Label>Google Maps URL</Label>
                  <Input
                    value={listing.location?.google_maps_url || ''}
                    onChange={(e) => setListing({
                      ...listing,
                      location: { ...listing.location, google_maps_url: e.target.value }
                    })}
                    placeholder="Paste Google Maps link"
                  />
                </div>

                {/* Map Preview */}
                {listing.location?.latitude && listing.location?.longitude && (
                  <div className="border rounded-lg overflow-hidden">
                    <iframe
                      src={`https://www.google.com/maps/embed/v1/place?key=AIzaSyBFw0Qbyq9zTFTd-tUY6dZWTgaQzuU17R8&q=${listing.location.latitude},${listing.location.longitude}&zoom=15`}
                      width="100%"
                      height="300"
                      style={{ border: 0 }}
                      allowFullScreen
                      loading="lazy"
                      title="Property Location"
                    />
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Nearby Places */}
            <Card className="mt-6">
              <CardHeader>
                <div className="flex items-center justify-between">
                  <div>
                    <CardTitle className="flex items-center gap-2">
                      <Navigation className="h-5 w-5 text-orange-500" />
                      Nearby Places
                    </CardTitle>
                    <CardDescription>Search and add nearby landmarks, transport hubs, and attractions using AI</CardDescription>
                  </div>
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => setListing({
                      ...listing,
                      nearby_places: [
                        ...(listing.nearby_places || []),
                        { name: '', type: 'landmark', distance: '', distance_unit: 'km', latitude: null, longitude: null, google_maps_url: '' }
                      ]
                    })}
                  >
                    <Plus className="h-4 w-4 mr-1" />
                    Add Manually
                  </Button>
                </div>
              </CardHeader>
              <CardContent>
                {/* AI Search Section */}
                <div className="bg-gradient-to-r from-purple-50 to-blue-50 border border-purple-200 rounded-lg p-4 mb-6">
                  <div className="flex items-center gap-2 mb-3">
                    <Sparkles className="h-5 w-5 text-purple-500" />
                    <span className="font-semibold text-purple-700">AI-Powered Place Search</span>
                  </div>
                  <div className="flex flex-col md:flex-row gap-3">
                    <Input
                      className="flex-1"
                      placeholder="Search for places (e.g., airport, shopping mall, hospital...)"
                      value={placeSearchQuery}
                      onChange={(e) => setPlaceSearchQuery(e.target.value)}
                      onKeyDown={(e) => e.key === 'Enter' && searchNearbyPlaces()}
                      data-testid="place-search-input"
                    />
                    <Select value={placeSearchType || "any"} onValueChange={(val) => setPlaceSearchType(val === "any" ? "" : val)}>
                      <SelectTrigger className="w-full md:w-[150px]">
                        <SelectValue placeholder="Any Type" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="any">Any Type</SelectItem>
                        <SelectItem value="airport">Airport</SelectItem>
                        <SelectItem value="railway">Railway</SelectItem>
                        <SelectItem value="bus">Bus Station</SelectItem>
                        <SelectItem value="hospital">Hospital</SelectItem>
                        <SelectItem value="shopping">Shopping</SelectItem>
                        <SelectItem value="restaurant">Restaurant</SelectItem>
                        <SelectItem value="landmark">Landmark</SelectItem>
                        <SelectItem value="beach">Beach</SelectItem>
                      </SelectContent>
                    </Select>
                    <Button 
                      onClick={searchNearbyPlaces} 
                      disabled={searchingPlaces}
                      className="bg-purple-600 hover:bg-purple-700"
                      data-testid="place-search-btn"
                    >
                      {searchingPlaces ? (
                        <Loader2 className="h-4 w-4 mr-1 animate-spin" />
                      ) : (
                        <Search className="h-4 w-4 mr-1" />
                      )}
                      Search
                    </Button>
                  </div>
                  
                  {/* Search Results */}
                  {showSearchResults && (
                    <div className="mt-4">
                      {searchingPlaces ? (
                        <div className="text-center py-4 text-purple-600">
                          <Loader2 className="h-6 w-6 mx-auto animate-spin mb-2" />
                          <p>Searching with AI...</p>
                        </div>
                      ) : placeSearchResults.length > 0 ? (
                        <div className="space-y-2 max-h-64 overflow-y-auto">
                          <p className="text-sm text-purple-600 mb-2">Found {placeSearchResults.length} places - click to add:</p>
                          {placeSearchResults.map((result, idx) => (
                            <div 
                              key={idx}
                              onClick={() => addPlaceFromSearch(result)}
                              className="flex items-center justify-between p-3 bg-white rounded-lg border border-purple-100 cursor-pointer hover:border-purple-300 hover:bg-purple-50 transition-all"
                              data-testid={`search-result-${idx}`}
                            >
                              <div className="flex-1">
                                <div className="font-medium text-gray-800">{result.name}</div>
                                <div className="text-sm text-gray-500">{result.address}</div>
                              </div>
                              <div className="flex items-center gap-3">
                                {result.distance_km && (
                                  <Badge variant="secondary" className="bg-blue-100 text-blue-700">
                                    {result.distance_km} km
                                  </Badge>
                                )}
                                {result.rating && (
                                  <Badge variant="secondary" className="bg-yellow-100 text-yellow-700">
                                    ⭐ {result.rating}
                                  </Badge>
                                )}
                                <Button size="sm" variant="ghost" className="text-purple-600 hover:bg-purple-100">
                                  <Plus className="h-4 w-4" />
                                </Button>
                              </div>
                            </div>
                          ))}
                        </div>
                      ) : (
                        <div className="text-center py-4 text-gray-500">
                          <MapPin className="h-8 w-8 mx-auto text-gray-300 mb-2" />
                          <p>No places found. Try a different search term.</p>
                        </div>
                      )}
                    </div>
                  )}
                </div>

                {/* Added Places List */}
                {(!listing.nearby_places || listing.nearby_places.length === 0) ? (
                  <div className="border-2 border-dashed rounded-lg p-8 text-center bg-gray-50">
                    <Landmark className="h-10 w-10 mx-auto text-gray-300 mb-2" />
                    <p className="text-gray-500">No nearby places added yet</p>
                    <p className="text-sm text-gray-400 mt-1">Use AI search above to find and add places</p>
                  </div>
                ) : (
                  <div className="space-y-4">
                    <p className="text-sm font-medium text-gray-700">{listing.nearby_places.length} places added:</p>
                    {listing.nearby_places.map((place, index) => (
                      <div key={index} className="border rounded-lg overflow-hidden">
                        <div className="flex items-center gap-3 p-3 bg-white">
                          <Select
                            value={place.type}
                            onValueChange={(value) => {
                              const updated = [...listing.nearby_places];
                              updated[index] = { ...place, type: value };
                              setListing({ ...listing, nearby_places: updated });
                            }}
                          >
                            <SelectTrigger className="w-[130px]">
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="airport"><Plane className="h-3 w-3 inline mr-1" /> Airport</SelectItem>
                              <SelectItem value="railway"><Train className="h-3 w-3 inline mr-1" /> Railway</SelectItem>
                              <SelectItem value="bus"><Bus className="h-3 w-3 inline mr-1" /> Bus Station</SelectItem>
                              <SelectItem value="hospital"><Hospital className="h-3 w-3 inline mr-1" /> Hospital</SelectItem>
                              <SelectItem value="shopping"><ShoppingBag className="h-3 w-3 inline mr-1" /> Shopping</SelectItem>
                              <SelectItem value="restaurant"><Utensils className="h-3 w-3 inline mr-1" /> Restaurant</SelectItem>
                              <SelectItem value="landmark"><Landmark className="h-3 w-3 inline mr-1" /> Landmark</SelectItem>
                              <SelectItem value="beach"><Globe className="h-3 w-3 inline mr-1" /> Beach</SelectItem>
                              <SelectItem value="other"><MapPin className="h-3 w-3 inline mr-1" /> Other</SelectItem>
                            </SelectContent>
                          </Select>

                          <Input
                            className="flex-1"
                            placeholder="Place name"
                            value={place.name}
                            onChange={(e) => {
                              const updated = [...listing.nearby_places];
                              updated[index] = { ...place, name: e.target.value };
                              setListing({ ...listing, nearby_places: updated });
                            }}
                          />

                          <Input
                            className="w-20"
                            type="number"
                            placeholder="Dist"
                            value={place.distance}
                            onChange={(e) => {
                              const updated = [...listing.nearby_places];
                              updated[index] = { ...place, distance: e.target.value };
                              setListing({ ...listing, nearby_places: updated });
                            }}
                          />

                          <Select
                            value={place.distance_unit}
                            onValueChange={(value) => {
                              const updated = [...listing.nearby_places];
                              updated[index] = { ...place, distance_unit: value };
                              setListing({ ...listing, nearby_places: updated });
                            }}
                          >
                            <SelectTrigger className="w-[70px]">
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="km">km</SelectItem>
                              <SelectItem value="m">m</SelectItem>
                              <SelectItem value="mi">mi</SelectItem>
                            </SelectContent>
                          </Select>

                          <Button
                            variant="ghost"
                            size="icon"
                            className="text-red-500"
                            onClick={() => {
                              const updated = listing.nearby_places.filter((_, i) => i !== index);
                              setListing({ ...listing, nearby_places: updated });
                            }}
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </div>

                        {/* Location Fields */}
                        <div className="border-t bg-gray-50 p-3">
                          <div className="grid grid-cols-3 gap-3">
                            <div>
                              <Label className="text-xs text-gray-500">Latitude</Label>
                              <Input
                                type="number"
                                step="any"
                                placeholder="12.9716"
                                value={place.latitude || ''}
                                onChange={(e) => {
                                  const updated = [...listing.nearby_places];
                                  updated[index] = { ...place, latitude: parseFloat(e.target.value) || null };
                                  setListing({ ...listing, nearby_places: updated });
                                }}
                                className="h-8"
                              />
                            </div>
                            <div>
                              <Label className="text-xs text-gray-500">Longitude</Label>
                              <Input
                                type="number"
                                step="any"
                                placeholder="77.5946"
                                value={place.longitude || ''}
                                onChange={(e) => {
                                  const updated = [...listing.nearby_places];
                                  updated[index] = { ...place, longitude: parseFloat(e.target.value) || null };
                                  setListing({ ...listing, nearby_places: updated });
                                }}
                                className="h-8"
                              />
                            </div>
                            <div>
                              <Label className="text-xs text-gray-500">Google Maps URL</Label>
                              <Input
                                placeholder="Paste Maps link"
                                value={place.google_maps_url || ''}
                                onChange={(e) => {
                                  const updated = [...listing.nearby_places];
                                  updated[index] = { ...place, google_maps_url: e.target.value };
                                  setListing({ ...listing, nearby_places: updated });
                                }}
                                className="h-8"
                              />
                            </div>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Amenities Tab */}
          <TabsContent value="amenities">
            <Card>
              <CardHeader>
                <CardTitle>Property Amenities</CardTitle>
                <CardDescription>Select amenities offered at your property</CardDescription>
              </CardHeader>
              <CardContent>
                {availableAmenities.length === 0 ? (
                  <div className="text-center py-8 bg-gray-50 rounded-lg border-2 border-dashed">
                    <CheckCircle className="h-10 w-10 mx-auto text-gray-300 mb-2" />
                    <p className="text-gray-500">No amenities configured</p>
                  </div>
                ) : (
                  <div className="grid grid-cols-3 gap-3">
                    {availableAmenities.map((amenity) => {
                      const isSelected = listing.amenities?.includes(amenity.id);
                      return (
                        <div
                          key={amenity.id}
                          className={`p-3 border rounded-lg cursor-pointer transition-all ${
                            isSelected ? 'border-orange-500 bg-orange-50' : 'hover:border-gray-300'
                          }`}
                          onClick={() => {
                            const current = listing.amenities || [];
                            const updated = isSelected
                              ? current.filter(id => id !== amenity.id)
                              : [...current, amenity.id];
                            setListing({ ...listing, amenities: updated });
                          }}
                        >
                          <div className="flex items-center gap-2">
                            <span className="text-lg">{amenity.icon}</span>
                            <span className="text-sm font-medium">{amenity.name}</span>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Photos Tab */}
          <TabsContent value="photos">
            <Card>
              <CardHeader>
                <CardTitle>Property Photos</CardTitle>
                <CardDescription>Upload photos of your property</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  <input
                    type="file"
                    ref={hotelPhotoInputRef}
                    className="hidden"
                    multiple
                    accept="image/*"
                    onChange={handleHotelPhotoUpload}
                  />
                  <Button
                    variant="outline"
                    onClick={() => hotelPhotoInputRef.current?.click()}
                    disabled={uploadingHotelPhoto}
                  >
                    {uploadingHotelPhoto ? (
                      <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    ) : (
                      <Upload className="h-4 w-4 mr-2" />
                    )}
                    Upload Photos
                  </Button>

                  {listing.listing_photos?.length > 0 ? (
                    <div className="grid grid-cols-4 gap-4">
                      {listing.listing_photos.map((photo, index) => (
                        <div key={index} className="relative group">
                          <img
                            src={photo.startsWith('http') ? photo : `${BACKEND_URL}${photo}`}
                            alt={`Photo ${index + 1}`}
                            className="w-full h-32 object-cover rounded-lg"
                          />
                          <button
                            className="absolute top-2 right-2 bg-red-500 text-white p-1 rounded-full opacity-0 group-hover:opacity-100 transition-opacity"
                            onClick={() => {
                              const updated = listing.listing_photos.filter((_, i) => i !== index);
                              setListing({ ...listing, listing_photos: updated });
                            }}
                          >
                            <X className="h-4 w-4" />
                          </button>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className="border-2 border-dashed rounded-lg p-8 text-center bg-gray-50">
                      <Image className="h-10 w-10 mx-auto text-gray-300 mb-2" />
                      <p className="text-gray-500">No photos uploaded</p>
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Rooms Tab */}
          <TabsContent value="rooms">
            <Card>
              <CardHeader>
                <CardTitle>Room Types</CardTitle>
                <CardDescription>Configure room types and availability</CardDescription>
              </CardHeader>
              <CardContent>
                {listing.room_types?.length > 0 ? (
                  <div className="space-y-4">
                    {listing.room_types.map((room) => (
                      <div key={room.id} className="border rounded-lg p-4">
                        <div className="flex items-center justify-between">
                          <div>
                            <h4 className="font-medium">{room.name}</h4>
                            <p className="text-sm text-gray-500">{room.description}</p>
                          </div>
                          <Badge variant="outline">
                            {room.total_rooms} rooms
                          </Badge>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-8 bg-gray-50 rounded-lg border-2 border-dashed">
                    <Bed className="h-10 w-10 mx-auto text-gray-300 mb-2" />
                    <p className="text-gray-500">No room types configured</p>
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Pricing Tab */}
          <TabsContent value="pricing">
            <Card>
              <CardHeader>
                <CardTitle>Room Pricing</CardTitle>
                <CardDescription>Configure pricing for each room type</CardDescription>
              </CardHeader>
              <CardContent>
                <p className="text-gray-500 text-center py-8">
                  Pricing configuration coming soon
                </p>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Discounts Tab */}
          <TabsContent value="discounts">
            <Card>
              <CardHeader>
                <CardTitle>Discount Offers</CardTitle>
                <CardDescription>Create special offers and promotions</CardDescription>
              </CardHeader>
              <CardContent>
                <p className="text-gray-500 text-center py-8">
                  Discount configuration coming soon
                </p>
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
}
